import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'hot'>('latest');

  const category = categories.find((c) => c.slug === slug);

  const filteredArticles = useMemo(() => {
    const filtered = articles.filter((a) => a.categorySlug === slug);
    if (sortBy === 'hot') {
      return [...filtered].sort((a, b) => b.views - a.views);
    }
    return filtered;
  }, [slug, sortBy]);

  const categoryName = category?.name || '未知分类';
  const categoryDesc = category?.description || '';

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <header>
        <Navbar />
      </header>
      <main>
        {/* Category Header */}
        <section className="pt-24 md:pt-28 pb-8 md:pb-12 px-4 md:px-8 lg:px-12 bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-3">
                {categoryName}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                {categoryDesc}
              </p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-4 md:py-6 px-4 md:px-8 lg:px-12 border-b border-background-200">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-400">
                共 {filteredArticles.length} 篇文章
              </span>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                type="button"
                onClick={() => setSortBy('latest')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  sortBy === 'latest'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                最新发布
              </button>
              <button
                type="button"
                onClick={() => setSortBy('hot')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  sortBy === 'hot'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                热度最高
              </button>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-8 md:py-12 px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <article
                    key={article.id}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300"
                  >
                    <Link to={`/article/${article.slug}`} className="block">
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          width="800"
                          height="500"
                        />
                        {article.isVideo && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/90 text-primary-500">
                              <i className="ri-play-fill text-xl" />
                            </div>
                          </div>
                        )}
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 leading-snug mb-3 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <div className="flex items-center gap-3">
                            <span>{article.author}</span>
                            <span>{article.date}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-eye-line" />
                            </span>
                            <span>{article.views.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-article-line text-4xl" />
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无文章</h3>
                <p className="text-sm text-foreground-400">该分类下暂时没有文章，去看看其他分类吧</p>
                <Link
                  to="/"
                  className="inline-block mt-4 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}